import type { PathEntry } from "./path.ts"
import type { PaintCells } from "./world.ts"
import { isCellIndex, isCellValue, isCount } from "./world.ts"

/**
 * The downstream wire, read by hand: there is no schema module shared with the
 * server, since the browser has no Effect. Anything that is not a message this
 * page knows is dropped. Every message is a complete state write (ADR 0002).
 */

export type WalkState = "walking" | "standing" | "resting"

export interface WalkMessage {
  readonly t: "walk"
  readonly state: WalkState
  readonly cell: number
  readonly until?: number
}

/** The Fuckery switch's three positions, in order. */
export const MOODS = ["restrained", "loose", "wildest"] as const
export type Mood = (typeof MOODS)[number]

export interface CtlMessage {
  readonly t: "ctl"
  readonly bpm: number
  readonly temp: number
  readonly swing: number
  readonly mood: Mood
  readonly by: number
}

export interface SnapshotMessage {
  readonly t: "snapshot"
  readonly you: number
  readonly gen: number
  readonly grid: string
  readonly walk: WalkMessage
  readonly ctl: CtlMessage
  readonly path: ReadonlyArray<PathEntry>
}

export interface CellMessage {
  readonly t: "cell"
  readonly by: number
  readonly cells: PaintCells
}

export interface PathMessage {
  readonly t: "path"
  readonly entries: ReadonlyArray<PathEntry>
}

/**
 * The full set of visible cursors, Visitor id to Cell, this page's own
 * included: the page leaves out its own id. An id absent has no cursor; there
 * is no leave message.
 */
export type CursorSet = Readonly<Record<string, number>>

export interface CursorsMessage {
  readonly t: "cursors"
  readonly c: CursorSet
}

export interface PongMessage {
  readonly t: "pong"
  readonly a: number
  readonly s: number
}

export type Downstream =
  | SnapshotMessage
  | CellMessage
  | CtlMessage
  | PathMessage
  | WalkMessage
  | CursorsMessage
  | PongMessage

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value)

const isEntry = (value: unknown): value is PathEntry =>
  isRecord(value) &&
  isNumber(value.gen) &&
  isNumber(value.step) &&
  isNumber(value.count) &&
  isCount(value.count) &&
  isNumber(value.cell) &&
  isCellIndex(value.cell) &&
  isNumber(value.due) &&
  (value.late === undefined || typeof value.late === "boolean") &&
  (value.odds === undefined || isRecord(value.odds)) &&
  (value.forms === undefined ||
    (Array.isArray(value.forms) && value.forms.every((form) => typeof form === "string")))

const isEntries = (value: unknown): value is ReadonlyArray<PathEntry> =>
  Array.isArray(value) && value.every(isEntry)

const isWalk = (value: unknown): value is WalkMessage =>
  isRecord(value) &&
  value.t === "walk" &&
  (value.state === "walking" || value.state === "standing" || value.state === "resting") &&
  isNumber(value.cell) &&
  isCellIndex(value.cell) &&
  (value.until === undefined || isNumber(value.until))

const isCtl = (value: unknown): value is CtlMessage =>
  isRecord(value) &&
  value.t === "ctl" &&
  isNumber(value.bpm) &&
  isNumber(value.temp) &&
  isNumber(value.swing) &&
  MOODS.some((mood) => mood === value.mood) &&
  isNumber(value.by)

const isCells = (value: unknown): value is PaintCells =>
  Array.isArray(value) &&
  value.every(
    (pair: unknown) =>
      Array.isArray(pair) &&
      pair.length === 2 &&
      pair.every(isNumber) &&
      isCellIndex(pair[0] as number) &&
      isCellValue(pair[1] as number)
  )

const isCursorSet = (value: unknown): value is CursorSet =>
  isRecord(value) &&
  Object.entries(value).every(([id, cell]) => /^\d+$/.test(id) && isNumber(cell) && isCellIndex(cell))

/** Parse one frame into a message this page knows, or nothing. */
export const readDownstream = (frame: unknown): Downstream | undefined => {
  if (typeof frame !== "string") return undefined
  let value: unknown
  try {
    value = JSON.parse(frame)
  } catch {
    return undefined
  }
  if (!isRecord(value)) return undefined
  switch (value.t) {
    case "snapshot":
      return isNumber(value.you) &&
        isNumber(value.gen) &&
        typeof value.grid === "string" &&
        isWalk(value.walk) &&
        isCtl(value.ctl) &&
        isEntries(value.path)
        ? (value as unknown as SnapshotMessage)
        : undefined
    case "cell":
      return isNumber(value.by) && isCells(value.cells) ? (value as unknown as CellMessage) : undefined
    case "ctl":
      return isCtl(value) ? value : undefined
    case "path":
      return isEntries(value.entries) ? (value as unknown as PathMessage) : undefined
    case "walk":
      return isWalk(value) ? value : undefined
    case "cursors":
      return isCursorSet(value.c) ? (value as unknown as CursorsMessage) : undefined
    case "pong":
      return isNumber(value.a) && isNumber(value.s) ? (value as unknown as PongMessage) : undefined
    default:
      return undefined
  }
}

/** Upstream, browser to server. Commands: verbs, partial, never echoed. */
export type Upstream =
  | { readonly t: "join" }
  | { readonly t: "present"; readonly v: boolean }
  | { readonly t: "paint"; readonly cells: PaintCells }
  /** The Cell under the pointer, or -1 when it leaves the Grid or the tab hides. */
  | { readonly t: "cursor"; readonly c: number }
  /** One knob per message, one per frame while dragging. */
  | { readonly t: "set"; readonly bpm: number }
  | { readonly t: "set"; readonly temp: number }
  | { readonly t: "set"; readonly swing: number }
  | { readonly t: "set"; readonly mood: Mood }
  | { readonly t: "ping"; readonly a: number }
